import { format, parseISO } from "date-fns";

export function formatDateTime(timestamp?: number) {
  if (!timestamp) {
    return "n/a";
  }

  return new Date(timestamp).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** Date-only values are calendar dates, not instants, so they parse in local
 * time. Date-times and epoch values represent instants in the viewer's local
 * timezone. */
export function parseFieldDate(value: unknown): Date | null {
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string" && typeof value !== "number") return null;
  if (typeof value === "string" && !value) return null;
  const parsed =
    typeof value === "string" && value.length <= 10
      ? parseISO(value)
      : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** The canonical record-field date format used by tables, cards, threads and
 * feed change summaries. */
export function formatFieldDate(
  value: unknown,
  withTime: boolean,
): string | null {
  const parsed = parseFieldDate(value);
  if (!parsed) return null;
  return format(parsed, withTime ? "d MMM yyyy, HH:mm" : "d MMM yyyy");
}

export function formatRelativeTime(timestamp?: number, now = Date.now()) {
  if (!timestamp) {
    return "-";
  }

  const diffMs = timestamp - now;
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs < hour) {
    return rtf.format(Math.round(diffMs / minute), "minute");
  }
  if (abs < day) {
    return rtf.format(Math.round(diffMs / hour), "hour");
  }
  return rtf.format(Math.round(diffMs / day), "day");
}

/** An Inbox item's arrival relative to `now`: "2 minutes ago", "yesterday",
 * then from a week on its date ("Sep 04"). */
export function relativeInboxTime(
  receivedAt: string,
  now = new Date(),
): string {
  const received = new Date(receivedAt);
  const diffMs = now.getTime() - received.getTime();
  if (!Number.isFinite(diffMs)) return "";
  const absMs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (absMs < 60_000) return rtf.format(Math.round(-diffMs / 1_000), "second");
  if (absMs < 3_600_000)
    return rtf.format(Math.round(-diffMs / 60_000), "minute");
  if (absMs < 86_400_000)
    return rtf.format(Math.round(-diffMs / 3_600_000), "hour");
  if (absMs < 604_800_000)
    return rtf.format(Math.round(-diffMs / 86_400_000), "day");
  return new Intl.DateTimeFormat("en", {
    day: "2-digit",
    month: "short",
  }).format(received);
}

/** Elapsed duration only: "3 mins ago", "2 hours ago", "1 day ago".
 * Never calendar words such as yesterday/today. */
export function formatElapsedAgo(timestamp?: number, now = Date.now()) {
  if (!timestamp) {
    return "-";
  }

  const elapsed = Math.max(0, now - timestamp);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (elapsed < hour) {
    const mins = Math.max(1, Math.floor(elapsed / minute));
    return mins === 1 ? "1 min ago" : `${mins} mins ago`;
  }
  if (elapsed < day) {
    const hours = Math.floor(elapsed / hour);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }
  const days = Math.floor(elapsed / day);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

const ISO_CURRENCY_CODE = /^[A-Z]{3}$/u;
const COMPACT_SUFFIXES: Readonly<Record<string, string>> = {
  K: "k",
  M: "m",
  B: "bn",
};

function normalizeCompactSuffix(value: string): string {
  return value.replace(
    /([KMB])$/u,
    (suffix) => COMPACT_SUFFIXES[suffix] ?? suffix,
  );
}

/*
 * One compact-number formatter for the app: Intl-backed so currency renders
 * with its real symbol (en-GB compact suffixes: k / m / bn). Non-ISO
 * "currency" strings (legacy symbol configs) fall back to a plain prefix.
 */
export function formatCompactValue(
  value: number,
  options: { currency?: string | null } = {},
): string {
  if (!Number.isFinite(value)) {
    return "-";
  }

  const currency = options.currency ?? null;
  const isoCurrency =
    currency && ISO_CURRENCY_CODE.test(currency) ? currency : null;
  const formatted = normalizeCompactSuffix(
    new Intl.NumberFormat("en-GB", {
      notation: "compact",
      compactDisplay: "short",
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
      ...(isoCurrency
        ? {
            style: "currency" as const,
            currency: isoCurrency,
            currencyDisplay: "narrowSymbol" as const,
          }
        : {}),
    }).format(value),
  );
  return currency && !isoCurrency ? `${currency} ${formatted}` : formatted;
}

/** How long a step or call took, as canvases show it: "432 ms", "1.3 s", "2 min 5 s". */
export function formatStepDuration(ms: number | undefined): string {
  if (ms === undefined) return "";
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  return seconds < 60 ? `${seconds.toFixed(1)} s` : `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} s`;
}

/** Clock time for a row that already sits under a day header. */
export function formatClockTime(timestamp: number) {
  return new Date(timestamp).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** A comment's time: the clock today, the date and clock otherwise. */
export function formatFeedTime(timestamp: number, now = Date.now()) {
  const date = new Date(timestamp);
  const today = new Date(now);
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  if (sameDay) return formatClockTime(timestamp);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/** "3 files", "1 file", "12+ files"; pass a plural for irregular nouns. */
export function countLabel(
  count: number,
  noun: string,
  options: { plural?: string; more?: boolean } = {},
): string {
  const word = count === 1 ? noun : (options.plural ?? `${noun}s`);
  return `${count}${options.more ? "+" : ""} ${word}`;
}
